 <template>
  <div class="card bg-base-100 card-bordered">
    <div class="card-body">
      <div class="flex justify-between items-center mb-6">
        <h3 class="card-title text-xl">
          <i class="mdi mdi-account-clock text-primary"></i>
          Pending Join Requests
        </h3>
        <button 
          @click="refreshRequests"
          class="btn btn-ghost btn-sm"
          :disabled="loading"
        >
          <i class="mdi mdi-refresh"></i>
          Refresh
        </button>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-8">
        <span class="loading loading-spinner loading-lg"></span>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="alert alert-error">
        <i class="mdi mdi-alert-circle h-6 w-6"></i>
        <span>{{ error }}</span>
      </div>

      <!-- No Requests -->
      <div v-else-if="pendingRequests.length === 0" class="text-center py-8">
        <div class="max-w-md mx-auto">
          <div class="text-4xl mb-4">✅</div>
          <h4 class="text-lg font-semibold mb-2">No pending requests</h4>
          <p class="text-base-content/70">
            All join requests have been processed.
          </p>
        </div>
      </div>

      <!-- Requests List -->
      <div v-else class="space-y-4">
        <div 
          v-for="request in pendingRequests" 
          :key="request.id"
          class="card bg-base-200 border-l-4 border-l-warning"
        >
          <div class="card-body p-4">
            <div class="flex justify-between items-start">
              <div class="flex-1">
                <h4 class="font-semibold text-lg">{{ request.display_name }}</h4>
                <div class="text-sm text-base-content/70 space-y-1 mt-2">
                  <div class="flex items-center gap-2">
                    <i class="mdi mdi-account"></i>
                    <span>{{ request.full_name }}</span>
                  </div>
                  <div v-if="request.email" class="flex items-center gap-2">
                    <i class="mdi mdi-email"></i>
                    <span>{{ request.email }}</span>
                  </div>
                  <div class="flex items-center gap-2">
                    <i class="mdi mdi-clock"></i>
                    <span>Requested {{ formatDate(request.created_at) }}</span>
                  </div>
                </div>
                
                <!-- Stock Picks Display -->
                <div v-if="request.entry && request.entry.stock_picks && request.entry.stock_picks.length > 0" class="mt-3">
                  <div class="text-sm font-medium text-base-content/80 mb-2">
                    <i class="mdi mdi-chart-line mr-1"></i>
                    Stock Picks:
                  </div>
                  <div class="flex flex-wrap gap-2">
                    <div 
                      v-for="pick in request.entry.stock_picks" 
                      :key="pick.symbol"
                      class="badge badge-outline badge-sm"
                    >
                      <span class="font-mono">{{ pick.symbol }}</span>
                      <span v-if="pick.weight" class="ml-1 text-xs">
                        ({{ (pick.weight * 100).toFixed(1) }}%)
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="flex gap-2">
                <button 
                  @click="approveRequest(request.id)"
                  class="btn btn-success btn-sm"
                  :disabled="processingRequest === request.id"
                >
                  <span v-if="processingRequest === request.id" class="loading loading-spinner loading-xs"></span>
                  <i v-else class="mdi mdi-check"></i>
                  Approve
                </button>
                <button 
                  @click="rejectRequest(request.id)"
                  class="btn btn-error btn-sm"
                  :disabled="processingRequest === request.id"
                >
                  <span v-if="processingRequest === request.id" class="loading loading-spinner loading-xs"></span>
                  <i v-else class="mdi mdi-close"></i>
                  Reject
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useCompetitionStore } from '../stores/competition'

const props = defineProps({
  competitionId: {
    type: [String, Number],
    required: true
  }
})

const competitionStore = useCompetitionStore()

const loading = ref(false)
const error = ref('')
const pendingRequests = ref([])
const processingRequest = ref(null)

const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

const fetchPendingRequests = async () => {
  loading.value = true
  error.value = ''
  
  try {
    const requests = await competitionStore.fetchPendingRequests(props.competitionId)
    pendingRequests.value = requests
  } catch (err) {
    error.value = err.message || 'Failed to load pending requests'
  } finally {
    loading.value = false
  }
}

const approveRequest = async (requestId) => {
  processingRequest.value = requestId
  
  try {
    await competitionStore.approveJoinRequest(props.competitionId, requestId)
    // Remove the approved request from the list
    pendingRequests.value = pendingRequests.value.filter(req => req.id !== requestId)
  } catch (err) {
    error.value = err.message || 'Failed to approve request'
  } finally {
    processingRequest.value = null
  }
}

const rejectRequest = async (requestId) => {
  processingRequest.value = requestId
  
  try {
    await competitionStore.rejectJoinRequest(props.competitionId, requestId)
    // Remove the rejected request from the list
    pendingRequests.value = pendingRequests.value.filter(req => req.id !== requestId)
  } catch (err) {
    error.value = err.message || 'Failed to reject request'
  } finally {
    processingRequest.value = null
  }
}

const refreshRequests = () => {
  fetchPendingRequests()
}

onMounted(() => {
  fetchPendingRequests()
})
</script> 